// Lib
import { FC, memo, useCallback, useEffect, useMemo } from "react";
// Api
import {
  useGetLoyaltyCustomersQuery,
  useLazyGetLoyaltyCustomersQuery,
} from "rtkQuery/query/loyaltyAPI";
// Hooks
import { useExport, useNotification, useTable, useViewport } from "hooks";
// Types
import { ETable } from "types/tableFilters";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DescriptionBlackIcon, RightBurgerMenuIcon } from "icons";
// Components
import { DropDown, Export, Modal, Pagination, Table } from "components";
// Styled
import { FlexContainer } from "styled/Box";
import { Button } from "styled/Buttons";

import { columns } from "./config";

interface LoyaltyUsersModalProps {
  loyaltyUsers: { id: string; name: string };
  setLoyaltyUsersUsers: (value: { id: string; name: string }) => void;
}

export const LoyaltyUsersModal: FC<LoyaltyUsersModalProps> = memo(
  ({ loyaltyUsers, setLoyaltyUsersUsers }) => {
    const { openNotification } = useNotification();
    const { isDesktop } = useViewport();
    const { downloadCSV } = useExport();

    const {
      page,
      limit,
      setPage,
      setLimit,
      search,
      setSearch,
      debouncedSearch,
    } = useTable({
      name: ETable.TiersByUserList,
      removeQueryParams: true,
    });

    const [getExportCouponUsersData, { isLoading: isExportLoading }] =
      useLazyGetLoyaltyCustomersQuery();

    const { data: tableData, isFetching: isTableFetching } =
      useGetLoyaltyCustomersQuery(
        {
          id: loyaltyUsers.id,
          query: { page, limit, search: debouncedSearch },
        },
        { refetchOnMountOrArgChange: true, skip: !loyaltyUsers.id },
      );

    useEffect(() => {
      if (!loyaltyUsers?.id) {
        setLimit(10);
      }
    }, [loyaltyUsers]);

    const getExportData = async (): Promise<Record<string, unknown>[]> => {
      try {
        const data = await getExportCouponUsersData({
          id: loyaltyUsers?.id,
          query: {
            limit: tableData.totalCount,
            search: debouncedSearch,
          },
        }).unwrap();

        return data.items;
      } catch (error) {
        errorHandler({ error, openNotification });
      }
    };

    const dropDownMenuItems = useMemo(
      () => [
        {
          key: "1",
          label: "Export",
          onClick: () =>
            downloadCSV({
              fileName: loyaltyUsers.name,
              isDataLoading: isExportLoading,
              columns: columns,
              getExportData: getExportData,
            }),
        },
      ],
      [loyaltyUsers],
    );

    const handleClose = useCallback(() => {
      setSearch("");
      setPage(1);
      setLimit(10);
      setLoyaltyUsersUsers({ id: "", name: "" });
    }, []);

    return (
      <Modal
        width={900}
        open={!!loyaltyUsers?.id}
        title={loyaltyUsers.name}
        onClose={handleClose}
      >
        <FlexContainer $fullwidth $column $gap={16}>
          <Table
            isLoading={isTableFetching}
            header={{
              totalCount: tableData?.totalCount,
              search: {
                value: search,
                setValue: setSearch,
                placeholder: "Search by name, phone number or email",
              },
              suffix: isDesktop ? (
                <Export
                  type="Base"
                  fileName={loyaltyUsers.name}
                  isLoading={isExportLoading}
                  isDataLoading={isTableFetching}
                  isDisabled={!tableData?.totalCount}
                  columns={columns}
                  getExportData={getExportData}
                />
              ) : (
                <DropDown items={dropDownMenuItems} trigger={["click"]}>
                  <Button.SquaredIcon icon={<RightBurgerMenuIcon />} />
                </DropDown>
              ),
            }}
            columns={columns}
            empty={{
              icon: DescriptionBlackIcon,
              title: "No users to show.",
            }}
            dataSource={tableData?.items || []}
          />

          <Pagination
            limit={limit}
            page={page}
            setLimit={setLimit}
            setPage={setPage}
            showSizeChanger={false}
            totalItems={tableData?.totalCount}
          />
        </FlexContainer>
      </Modal>
    );
  },
);

LoyaltyUsersModal.displayName = "LoyaltyUsersModal";
